import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Round 2 (31 Oct 2026, Visits & Meetings party-type-first form): these optional-only
// questions collapse into each section's "More details" — nothing is ever dropped from
// the report, they're just one click away instead of mixed in with the required ones.
const MORE_DETAILS_CODES = new Set([
  'payment_terms_wanted', 'ships_from', 'stock_lists_frequency', // Vendor
  'vstl_share', 'values_in_vstl', 'must_improve', 'payment_terms_asked', 'payment_terms_asked_credit_days', // Customer
]);

// Round 2 (31 Oct 2026): a Vendor-only or Customer-only meeting gets ONE heading
// covering COMMON + its own side, per the proposal; a Both meeting keeps COMMON
// separate, then "As a vendor" before "As a customer".
function buildGroups(role) {
  if (role === 'VENDOR') return [{ key: 'VENDOR', label: 'Vendor details', sections: ['COMMON', 'VENDOR'] }, { key: 'PURPOSE', label: 'Purpose details', sections: ['PURPOSE'] }];
  if (role === 'CUSTOMER') return [{ key: 'CUSTOMER', label: 'Customer details', sections: ['COMMON', 'CUSTOMER'] }, { key: 'PURPOSE', label: 'Purpose details', sections: ['PURPOSE'] }];
  return [
    { key: 'COMMON', label: 'Common details', sections: ['COMMON'] },
    { key: 'VENDOR', label: 'As a vendor', sections: ['VENDOR'] },
    { key: 'CUSTOMER', label: 'As a customer', sections: ['CUSTOMER'] },
    { key: 'PURPOSE', label: 'Purpose details', sections: ['PURPOSE'] },
  ];
}

function sectionApplies(q, meeting) {
  if (q.section === 'CUSTOMER') return ['CUSTOMER', 'BOTH'].includes(meeting.counterparty_role);
  if (q.section === 'VENDOR') return ['VENDOR', 'BOTH'].includes(meeting.counterparty_role);
  if (q.section === 'PURPOSE') return (meeting.purposes || []).includes(q.purpose_code);
  return true;
}

function showIfMatches(showIf, answers) {
  if (!showIf) return true;
  const val = answers[showIf.code];
  const vals = Array.isArray(val) ? val : [val];
  // Multi-select answers (business_type, stock_lists — HR/Meetings round, 27 Oct
  // 2026): match if ANY ticked value is in the accepted list, not the whole array.
  return Array.isArray(showIf.equals) ? vals.some((v) => showIf.equals.includes(v)) : vals.includes(showIf.equals);
}

function needsDetail(q, val) {
  if (q.allow_other_text) return true;
  if (q.detail_required_unless === null || q.detail_required_unless === undefined) return false;
  const vals = Array.isArray(val) ? val : [val];
  return vals.some((v) => v !== q.detail_required_unless) && vals.length > 0;
}

function QuestionField({ q, value, detail, onChange, onDetailChange, productCategories, intro_sources, competitors, disabled }) {
  const dynamicOptions = { introduction_source: intro_sources, competitors }[q.code];
  const options = dynamicOptions || q.options || [];
  const set = (v) => onChange(v);

  let field;
  if (q.answer_type === 'YES_NO') {
    field = (
      <Select value={value || ''} onValueChange={set} disabled={disabled}>
        <SelectTrigger data-testid={`q-${q.code}`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
      </Select>
    );
  } else if (q.answer_type === 'SINGLE') {
    field = (
      <Select value={value || ''} onValueChange={set} disabled={disabled}>
        <SelectTrigger data-testid={`q-${q.code}`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  } else if (q.answer_type === 'CATEGORY_MULTI') {
    // Lex's Preview feedback point 2 (26 Oct 2026): VSTL's real product list, shown as
    // groups with their items — picking a group alone (with no item ticked) is allowed.
    // Fix (Part B8, 26 Oct 2026, review round 2C): the stored value is the CODE, not the
    // label (a label rename in Admin must never split the reports) — a category that's
    // since been deactivated still renders (with its label) if it's part of this answer,
    // but is hidden from fresh picking.
    // Lex's "ticking a group ticks all its items" ask (round 23, 26 Oct 2026): a group
    // with items cascades — ticking it selects every ACTIVE item under it (and the group
    // code itself is only saved once every active item is ticked); ticking only some
    // items leaves the group checkbox indeterminate and un-saved. Groups with no items
    // (Desktop Complete, Laptop, ...) keep behaving like a plain single checkbox.
    const arr = Array.isArray(value) ? value : [];
    const allCats = productCategories || [];
    const pickable = (c) => c.active !== false || arr.includes(c.code);
    const groups = allCats.filter((c) => !c.parent_code && pickable(c));
    const childrenOf = (code) => allCats.filter((c) => c.parent_code === code && pickable(c));
    const activeChildCodesOf = (code) => allCats.filter((c) => c.parent_code === code && c.active !== false).map((c) => c.code);
    const toggle = (code) => set(arr.includes(code) ? arr.filter((x) => x !== code) : [...arr, code]);

    const isGroupFull = (g) => {
      const codes = activeChildCodesOf(g.code);
      return codes.length > 0 && codes.every((code) => arr.includes(code));
    };
    const isGroupPartial = (g) => {
      const codes = activeChildCodesOf(g.code);
      if (codes.length === 0) return false;
      const ticked = codes.filter((code) => arr.includes(code)).length;
      return ticked > 0 && ticked < codes.length;
    };
    const toggleGroup = (g) => {
      const codes = activeChildCodesOf(g.code);
      if (codes.length === 0) { toggle(g.code); return; }
      const full = isGroupFull(g);
      const withoutGroupAndChildren = arr.filter((x) => x !== g.code && !codes.includes(x));
      set(full ? withoutGroupAndChildren : [...withoutGroupAndChildren, g.code, ...codes]);
    };
    const toggleItem = (g, c) => {
      let next = arr.includes(c.code) ? arr.filter((x) => x !== c.code) : [...arr, c.code];
      const codes = activeChildCodesOf(g.code);
      const allTicked = codes.length > 0 && codes.every((code) => next.includes(code));
      next = next.filter((x) => x !== g.code);
      if (allTicked) next = [...next, g.code];
      set(next);
    };
    field = (
      <div className="space-y-2 border rounded-sm p-2" data-testid={`q-${q.code}`}>
        {groups.map((g) => {
          const children = childrenOf(g.code);
          const hasChildren = children.length > 0;
          const checkedState = hasChildren ? (isGroupFull(g) ? true : (isGroupPartial(g) ? 'indeterminate' : false)) : arr.includes(g.code);
          return (
            <div key={g.code}>
              <label className={`flex items-center gap-1.5 text-sm font-medium ${disabled ? 'opacity-60' : 'cursor-pointer'}`} data-testid={`q-${q.code}-group-${g.code}`}>
                <Checkbox disabled={disabled} checked={checkedState} onCheckedChange={() => toggleGroup(g)} />
                {g.label}{g.active === false && <span className="text-xs text-muted-foreground"> (inactive)</span>}
              </label>
              {hasChildren && (
                <div className="flex flex-wrap gap-2 pl-6 mt-1">
                  {children.map((c) => (
                    <label key={c.code} className={`flex items-center gap-1.5 border rounded-sm px-2 py-1 text-xs ${disabled ? 'opacity-60' : 'cursor-pointer'}`} data-testid={`q-${q.code}-item-${c.code}`}>
                      <Checkbox disabled={disabled} checked={arr.includes(c.code)} onCheckedChange={() => toggleItem(g, c)} />
                      {c.label}{c.active === false && <span className="text-xs text-muted-foreground"> (inactive)</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  } else if (q.answer_type === 'MULTI') {
    const arr = Array.isArray(value) ? value : [];
    field = (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.code}`}>
        {options.map((o) => (
          <label key={o} className={`flex items-center gap-1.5 border rounded-sm px-2 py-1 text-sm ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
            <Checkbox disabled={disabled} checked={arr.includes(o)} onCheckedChange={() => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])} />
            {o}
          </label>
        ))}
      </div>
    );
  } else if (q.answer_type === 'LONG_TEXT') {
    field = <Textarea value={value || ''} onChange={(e) => set(e.target.value)} disabled={disabled} data-testid={`q-${q.code}`} />;
  } else if (q.answer_type === 'NUMBER' || q.answer_type === 'USD') {
    field = <Input type="number" value={value ?? ''} onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))} disabled={disabled} data-testid={`q-${q.code}`} />;
  } else if (q.answer_type === 'DATE') {
    field = <Input type="date" value={value || ''} onChange={(e) => set(e.target.value)} disabled={disabled} data-testid={`q-${q.code}`} />;
  } else if (q.answer_type === 'DATE_OR_NONE') {
    field = (
      <div className="flex items-center gap-2">
        <Input type="date" value={value === 'None' ? '' : (value || '')} onChange={(e) => set(e.target.value)} disabled={disabled} data-testid={`q-${q.code}`} className="w-44" />
        <label className={`flex items-center gap-1.5 text-sm ${disabled ? 'opacity-60' : ''}`}><Checkbox disabled={disabled} checked={value === 'None'} onCheckedChange={(v) => set(v ? 'None' : '')} /> None planned</label>
      </div>
    );
  } else if (q.answer_type === 'PART_LIST' || q.answer_type === 'PRICE_ROWS') {
    const arr = Array.isArray(value) ? value.join(', ') : (value || '');
    field = <Textarea value={arr} placeholder={q.answer_type === 'PRICE_ROWS' ? 'e.g. SKU123: $45, SKU456: $12' : 'Comma-separated part numbers'}
      onChange={(e) => set(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} disabled={disabled} data-testid={`q-${q.code}`} />;
  } else {
    field = <Input value={value || ''} onChange={(e) => set(e.target.value)} disabled={disabled} data-testid={`q-${q.code}`} />;
  }

  return (
    <div className="space-y-1">
      <Label>{q.label}{q.required && <span className="text-red-600"> *</span>}</Label>
      {q.help && <p className="text-xs text-muted-foreground">{q.help}</p>}
      {field}
      {needsDetail(q, value) && (
        <Input placeholder={q.allow_other_text ? 'Other / additional note' : 'Add a detail note'} value={detail || ''}
          onChange={(e) => onDetailChange(e.target.value)} disabled={disabled} data-testid={`q-${q.code}-detail`} />
      )}
    </div>
  );
}

// `answers` / `onChange` work with a plain {code: value} map for easy binding; parent
// converts to/from the API's [{code,value,detail}] shape right before save/submit.
export default function PointerQuestionsForm({ questions, meeting, answers, details, onAnswer, onDetail, settings, disabled = false }) {
  const visible = questions.filter((q) => sectionApplies(q, meeting) && (!q.physical_only || meeting.mode === 'PHYSICAL') && showIfMatches(q.show_if, answers));
  const bySection = visible.reduce((acc, q) => { (acc[q.section] = acc[q.section] || []).push(q); return acc; }, {});
  const groups = buildGroups(meeting.counterparty_role).filter((g) => g.sections.some((s) => (bySection[s] || []).length > 0));

  const renderField = (q) => (
    <QuestionField key={q.code} q={q} value={answers[q.code]} detail={details[q.code]} disabled={disabled}
      productCategories={settings.product_categories} intro_sources={settings.intro_sources} competitors={settings.competitors}
      onChange={(v) => onAnswer(q.code, v)} onDetailChange={(v) => onDetail(q.code, v)} />
  );

  return (
    <div className="space-y-6" data-testid="pointer-questions-form">
      {groups.map((g) => {
        const sorted = g.sections.flatMap((s) => bySection[s] || []).sort((a, b) => a.sort_order - b.sort_order);
        const main = sorted.filter((q) => q.required || !MORE_DETAILS_CODES.has(q.code));
        const more = sorted.filter((q) => !q.required && MORE_DETAILS_CODES.has(q.code));
        return (
          <div key={g.key} className="space-y-3" data-testid={`pointer-section-${g.key}`}>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{g.label}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{main.map(renderField)}</div>
            {more.length > 0 && (
              <details className="border rounded-sm p-3" data-testid={`pointer-section-${g.key}-more-details`}>
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground select-none">More details</summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">{more.map(renderField)}</div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}
